import { Divider } from "@mui/material";
import Banner from "components/banner";
import HeaderWithFilter from "components/headerwithfilter";
import CustomSnackbar from "components/snackbar";
import SurveyMatchPopup from "components/surveypopup";
import { MatchedSurvey } from "components/surveypopup/interface";
import { State } from "data/reducers/state";
import { SurveyListActions } from "data/slices/surveylist";
import { surveyIncreaseClicks } from "data/slices/surveylist/api";
import {
  IncreaseClickResponse,
  SurveyListStateProps,
} from "data/slices/surveylist/interface";
import keys from "locale/keys";
import { isEmpty, isUndefined } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DASHBOARD_ROUTES,
  MAX_SURVEYS_IN_DASHBOARD,
  SNACKBAR_TYPE,
  SURVEY_STATUS_ASSIGNED,
  SURVEY_STATUS_COMPLETED,
} from "utils/constants";
import {
  getBestMatchSurvey,
  sortSurveyItemsByTab,
  surveysMapper,
} from "utils/mappers/SurveysListMapper";
import { isMatchPopupShown, setMatchPopupShown } from "utils/Utils";
import SURVEYSPURE from "widgets/dashboard/surveys";
import {
  SortTabItem,
  SurveySetProps,
  SurveySortTab,
} from "widgets/dashboard/surveys/interface";
import NotificationBanner from "widgets/dashboard/surveys/NotificationBanner";
import SortTabs from "widgets/dashboard/surveys/SortTabs";

import { SurveysProps } from "../interface";
import { Wrapper } from "../styled.components";
import { ALLSURVEYSPURE } from "./AllSurveysContainer";

// While the survey list is on screen, refresh it periodically so newly
// assigned/completed surveys show up without a manual reload.
const SURVEY_POLL_INTERVAL_MS = 60000;

// If the tab is visible but the user hasn't touched the mouse/keyboard in
// this long, treat them as idle and stop polling until they interact again.
const SURVEY_IDLE_TIMEOUT_MS = 2 * 60 * 1000;

// Skip the "tab became visible" refetch if a fetch already happened this
// recently - avoids firing extra requests on rapid alt-tabbing.
const SURVEY_VISIBILITY_REFETCH_COOLDOWN_MS = 10000;

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
] as const;

const SurveysContainer = (props: SurveysProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [showAllSurveys, setShowAllSurveys] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showingCompletedSurveys, setShowingCompletedSurveys] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [showMatchPopup, setShowMatchPopup] = useState(false);
  const [matchedSurvey, setMatchedSurvey] = useState<MatchedSurvey | null>(
    null,
  );
  const [isSortTabSwitching, setIsSortTabSwitching] = useState(false);
  const sortTabSwitchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [selectedSortTab, setSelectedSortTab] = useState<SurveySortTab>(
    (location.state as { selectedSortTab?: SurveySortTab })?.selectedSortTab ??
      SurveySortTab.BEST_MATCH,
  );
  const [snackbarMsg, setSnackbarMsg] = useState({
    msg: "",
    type: SNACKBAR_TYPE.ERROR,
  });

  const {
    isFetchingNewSurveys,
    isFetchingCompletedSurveys,
    newsurveys,
    completedsurveys,
    isError,
  } = useSelector<State, SurveyListStateProps>((state) => state.surveylist);

  // Tracks when a fetch last actually happened (from any trigger - mount,
  // poll, idle-resume, or visibility return), so the visibility handler can
  // skip refetching if one just fired moments ago.
  const lastFetchAtRef = useRef<number>(0);

  const fetchNewSurveys = useCallback(() => {
    lastFetchAtRef.current = Date.now();
    dispatch(
      SurveyListActions.request({
        status: SURVEY_STATUS_ASSIGNED,
        pageSize: MAX_SURVEYS_IN_DASHBOARD + 1,
        page: 0,
      }),
    );
  }, [dispatch]);

  const fetchCompletedSurveys = useCallback(() => {
    lastFetchAtRef.current = Date.now();
    dispatch(
      SurveyListActions.request({
        status: SURVEY_STATUS_COMPLETED,
        pageSize: MAX_SURVEYS_IN_DASHBOARD + 1,
        page: 0,
      }),
    );
  }, [dispatch]);

  //in the main page, just fetch the initial surveys to show. If more are present, then show view more option to see all other surveys
  useEffect(() => {
    fetchNewSurveys();
    fetchCompletedSurveys();
  }, [fetchNewSurveys, fetchCompletedSurveys]);

  // Poll for updates only while this tab is the visible/active one, so a
  // survey opened in another tab (e.g. an external provider) doesn't cause
  // background requests here. When the user switches back to this tab we
  // refetch immediately - this is what picks up survey completion once the
  // provider redirects the user away and back, rather than waiting up to a
  // full interval - and then resume the regular interval. On top of that,
  // if the tab stays visible but untouched (no mouse/keyboard/scroll) for
  // SURVEY_IDLE_TIMEOUT_MS, polling pauses until the user interacts again.
  const pollIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const lastActivityRef = useRef<number>(Date.now());
  const isIdleRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = undefined;
    }
  }, []);

  const pollTick = useCallback(() => {
    const idleFor = Date.now() - lastActivityRef.current;
    if (idleFor >= SURVEY_IDLE_TIMEOUT_MS) {
      isIdleRef.current = true;
      stopPolling();
      return;
    }
    // Completed surveys don't change while someone is just browsing the
    // list, so routine polling only refreshes the new/assigned surveys.
    fetchNewSurveys();
  }, [fetchNewSurveys, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      return;
    }
    pollIntervalRef.current = setInterval(pollTick, SURVEY_POLL_INTERVAL_MS);
  }, [pollTick]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        lastActivityRef.current = Date.now();
        isIdleRef.current = false;
        // Coming back to the tab is when a survey may have just been
        // completed elsewhere, so refresh both lists here - unless a fetch
        // just happened moments ago (e.g. rapid alt-tabbing), in which case
        // it would be a wasted duplicate call.
        const timeSinceLastFetch = Date.now() - lastFetchAtRef.current;
        if (timeSinceLastFetch >= SURVEY_VISIBILITY_REFETCH_COOLDOWN_MS) {
          fetchNewSurveys();
          fetchCompletedSurveys();
        }
        startPolling();
      } else {
        stopPolling();
      }
    };

    if (document.visibilityState === "visible") {
      startPolling();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopPolling();
    };
  }, [fetchNewSurveys, fetchCompletedSurveys, startPolling, stopPolling]);

  // Track real user interaction so an open-but-untouched tab doesn't keep
  // polling forever. Coming back from idle triggers an immediate refetch.
  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      if (isIdleRef.current) {
        isIdleRef.current = false;
        fetchNewSurveys();
        startPolling();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, handleActivity, { passive: true }),
    );

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, handleActivity),
      );
    };
  }, [fetchNewSurveys, startPolling]);

  const newSurveys: SurveySetProps = surveysMapper(
    newsurveys.rows,
    false,
    isError,
    newsurveys.count,
  );
  newSurveys.surveyItems = sortSurveyItemsByTab(
    newSurveys.surveyItems,
    selectedSortTab,
  );
  const completedSurveys: SurveySetProps = surveysMapper(
    completedsurveys.rows,
    true,
    isError,
    completedsurveys.count,
  );

  // Show the "good match" popup once per login, the first time the surveys
  // page has finished loading with at least one available survey. If there
  // are no surveys yet, this keeps re-checking on later polls/visits within
  // the same login rather than permanently skipping the popup.
  useEffect(() => {
    if (isFetchingNewSurveys || isError) return;
    if (isMatchPopupShown()) return;
    if (newSurveys.surveyItems.length === 0) return;

    const bestMatch = getBestMatchSurvey(newSurveys.surveyItems);
    if (!bestMatch) return;

    setMatchedSurvey({
      id: bestMatch.id,
      surveyNumber: bestMatch.surveyNumber,
      points: bestMatch.points,
      timeToComplete: bestMatch.timeToComplete,
      url: bestMatch.url,
      providerKey: bestMatch.providerKey,
    });
    setShowMatchPopup(true);
    setMatchPopupShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingNewSurveys, isError, newSurveys.surveyItems]);

  const sortTabs: SortTabItem[] = [
    { label: t(keys.sort_best_match) },
    { label: t(keys.sort_short_surveys) },
    { label: t(keys.sort_most_points) },
    { label: t(keys.sort_max_points_per_min) },
    { label: t(keys.survey_profile) },
  ];

  const onSortTabChanged = (index: number) => {
    if (index === SurveySortTab.SURVEY_PROFILER) {
      navigate(`/dashboard/${DASHBOARD_ROUTES.SURVEY_PROFILE}`);
      return;
    }
    setSelectedSortTab(index);

    // Briefly show a loading state on tab switch so the resort feels like a
    // fresh fetch rather than an instant client-side re-sort.
    if (sortTabSwitchTimeoutRef.current) {
      clearTimeout(sortTabSwitchTimeoutRef.current);
    }
    setIsSortTabSwitching(true);
    sortTabSwitchTimeoutRef.current = setTimeout(() => {
      setIsSortTabSwitching(false);
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (sortTabSwitchTimeoutRef.current) {
        clearTimeout(sortTabSwitchTimeoutRef.current);
      }
    };
  }, []);

  const onViewMoreClicked = (isCompletedSurveys: boolean) => {
    setShowAllSurveys(true);
    setShowingCompletedSurveys(isCompletedSurveys);
  };

  const onAllSurveysBackClicked = () => {
    setShowAllSurveys(false);
  };

  const dismissNotification = () => {
    setShowNotification(false);
  };

  const increaseClicks = async (surveyId: number) => {
    setIsLoading(true);

    const response: IncreaseClickResponse =
      await surveyIncreaseClicks(surveyId);

    setIsLoading(false);
    if (response && !isEmpty(response?.link) && !isUndefined(response?.link)) {
      openLink(response.link);
    } else {
      setShowSnackbar(true);
      setSnackbarMsg({
        msg: response?.message || t(keys.survey_link_fetch_failed),
        type: SNACKBAR_TYPE.ERROR,
      });
    }
  };

  const onStartSurveyClicked = (url: string, key: string, id: number) => {
    increaseClicks(id);
  };

  const openLink = (link: string) => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const dismissMatchPopup = () => {
    setShowMatchPopup(false);
  };

  const onMatchSurveyStartClicked = (survey: MatchedSurvey) => {
    setShowMatchPopup(false);
    onStartSurveyClicked(survey.url || "", survey.providerKey || "", survey.id);
  };

  return (
    <>
      <SurveyMatchPopup
        show={showMatchPopup}
        dismiss={dismissMatchPopup}
        survey={matchedSurvey}
        onStartSurveyClicked={onMatchSurveyStartClicked}
      />
      {showAllSurveys && (
        <ALLSURVEYSPURE
          showingCompletedSurveys={showingCompletedSurveys}
          onAllSurveysBackClicked={onAllSurveysBackClicked}
          onStartSurveyClicked={onStartSurveyClicked}
          isLoading={isLoading}
        />
      )}
      {!showAllSurveys && (
        <>
          {showNotification && (
            <Banner>
              <NotificationBanner
                date="Dec 28, 2023"
                title="Survey Invitations"
                description="New Survey Available! Earn 50 points by completing this 10-minute survey on consumer habits."
                dismissNotification={dismissNotification}
              />
            </Banner>
          )}
          <HeaderWithFilter title={t(keys.answer_surveys)} showFilter={false} />
          <Divider />
          <Wrapper>
            <SortTabs
              tabs={sortTabs}
              selectedTab={selectedSortTab}
              onTabChanged={onSortTabChanged}
            />
            <SURVEYSPURE
              newSurveys={newSurveys}
              completedSurveys={completedSurveys}
              onViewMoreClicked={onViewMoreClicked}
              isNewSurveysLoading={isFetchingNewSurveys || isSortTabSwitching}
              isCompletedSurveysLoading={
                isFetchingCompletedSurveys || isSortTabSwitching
              }
              onStartSurveyClicked={onStartSurveyClicked}
              isError={isError}
              isLoading={isLoading}
            />
          </Wrapper>
          <CustomSnackbar
            msg={snackbarMsg.msg}
            type={snackbarMsg.type}
            show={showSnackbar}
            dismiss={() => setShowSnackbar(false)}
          />
        </>
      )}
    </>
  );
};

export default SurveysContainer;
